import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { SvgNmrOverlay } from 'cheminfo-font';
import { useCallback, useState, useRef, memo } from 'react';
import { FaFileExport } from 'react-icons/fa';
import { IoPulseOutline } from 'react-icons/io5';
import { getDataAsString, generateAnalyzeSpectra, } from '../../../data/data1d/multipleSpectraAnalysis';
import { ClipboardFallbackModal } from '../../../utils/clipboard/clipboardComponents';
import { useClipboard } from '../../../utils/clipboard/clipboardHooks';
import { useChartData } from '../../context/ChartContext';
import { useDispatch } from '../../context/DispatchContext';
import Button from '../../elements/ButtonToolTip';
import ToggleButton from '../../elements/ToggleButton';
import { positions, useAlert } from '../../elements/popup/Alert';
import { useModal } from '../../elements/popup/Modal';
import { usePanelPreferences } from '../../hooks/usePanelPreferences';
import AlignSpectraModal from '../../modal/AlignSpectraModal';
import { getSpectraByNucleus } from '../../utility/getSpectraByNucleus';
import { tablePanelStyle } from '../extra/BasicPanelStyle';
import DefaultPanelHeader from '../header/DefaultPanelHeader';
import PreferencesHeader from '../header/PreferencesHeader';
import MultipleSpectraAnalysisTable from './MultipleSpectraAnalysisTable';
import MultipleSpectraAnalysisPreferences from './preferences';
function MultipleSpectraAnalysisPanelInner({ activeTab, spectra, showLegend, }) {
    const [isFlipped, setFlipStatus] = useState(false);
    const spectraPreferences = usePanelPreferences('spectra', activeTab);
    const preferences = usePanelPreferences('multipleSpectraAnalysis', activeTab);
    const spectraAnalysis = generateAnalyzeSpectra(preferences, spectra, activeTab);
    const settingRef = useRef();
    const alert = useAlert();
    const modal = useModal();
    const dispatch = useDispatch();
    const settingsPanelHandler = useCallback(() => {
        setFlipStatus(!isFlipped);
    }, [isFlipped]);
    const saveSettingHandler = useCallback(() => {
        settingRef.current.saveSetting();
    }, []);
    const afterSaveHandler = useCallback(() => {
        setFlipStatus(false);
    }, []);
    const showTrackerHandler = useCallback(() => {
        dispatch({ type: 'TOGGLE_SPECTRA_LEGEND' });
    }, [dispatch]);
    const openAlignSpectra = useCallback(() => {
        dispatch({ type: 'RESET_SELECTED_TOOL' });
        modal.show(_jsx(AlignSpectraModal, { nucleus: activeTab }), {
            isBackgroundBlur: false,
            position: positions.TOP_CENTER,
            width: 500,
        });
    }, [activeTab, modal, dispatch]);
    const { rawWriteWithType, cleanShouldFallback, shouldFallback, text } = useClipboard();
    const copyToClipboardHandler = useCallback(() => {
        const data = getDataAsString(spectraAnalysis, spectra, spectraPreferences);
        if (!data)
            return;
        void rawWriteWithType(data).then(() => alert.success('Data copied to clipboard'));
    }, [alert, rawWriteWithType, spectra, spectraAnalysis, spectraPreferences]);
    return (_jsxs("div", { css: [
            tablePanelStyle,
            isFlipped &&
                css `
            .table-container th {
              position: relative;
            }
          `,
        ], children: [!isFlipped && (_jsxs(DefaultPanelHeader, { deleteToolTip: "Delete All Peaks", showSettingButton: true, canDelete: false, onSettingClick: settingsPanelHandler, children: [_jsx(Button, { popupTitle: "Copy To Clipboard", onClick: copyToClipboardHandler, children: _jsx(FaFileExport, {}) }), _jsx(Button, { popupTitle: "Spectra calibration", onClick: openAlignSpectra, children: _jsx(SvgNmrOverlay, { style: { fontSize: '18px' } }) }), _jsx(ToggleButton, { popupTitle: "Y spectra tracker", popupPlacement: "right", onClick: showTrackerHandler, defaultValue: showLegend, children: _jsx(IoPulseOutline, {}) }, `${showLegend}`)] })), isFlipped && (_jsx(PreferencesHeader, { onSave: saveSettingHandler, onClose: settingsPanelHandler })), _jsx("div", { className: "inner-container", children: !isFlipped ? (_jsx(MultipleSpectraAnalysisTable, { data: spectraAnalysis, resortSpectra: preferences.analysisOptions.resortSpectra, activeTab: activeTab })) : (_jsx(MultipleSpectraAnalysisPreferences, { data: spectraAnalysis, activeTab: activeTab, onAfterSave: afterSaveHandler, ref: settingRef })) }), _jsx(ClipboardFallbackModal, { mode: shouldFallback, onDismiss: cleanShouldFallback, text: text, label: "Spectra Analysis" })] }));
}
const MemoizedMultipleSpectraAnalysisPanel = memo(MultipleSpectraAnalysisPanelInner);
export default function MultipleSpectraAnalysisPanel() {
    const { data, view: { spectra: { activeTab, showLegend }, }, displayerKey, } = useChartData();
    const spectra = getSpectraByNucleus(activeTab, data);
    if (!activeTab) {
        return _jsx("div", {});
    }
    return (_jsx(MemoizedMultipleSpectraAnalysisPanel, { activeTab, displayerKey, spectra, showLegend }));
}
